import { DataGrid, DataGridColumn, DataGridRef, Spinner, ButtonV2 } from '@cimplico/components';
import { GenericWorksheet, ReconciliationWorksheet, WorksheetTypeEnum } from '../../../types/worksheet';
import { ROW_SIZES } from '../../../constants';
import EntryRow from './EntryRow';
import EntriesSubtotal from './EntriesSubtotal';
import { useMemo, useRef, useState } from 'react';
import NoEntriesMessage from './NoEntriesMessage';
import { useEntriesV2 } from '../../../queries/use-entries';
import { useWorkpaper } from '../../../queries/use-workpaper';
import { formatDate } from '../../../utilities/formatting';
import { ForwardIcon } from '@heroicons/react/24/outline';
import { useCarryForward } from '../../../queries/use-carry-forward';
import { toastNotification } from '../../../components/toast/Toast';
import { ResourceType } from '../../../types/carry-forward';
import { useIntercom } from 'react-use-intercom';
import { intercomEvents } from '../../../intercom-events';
import plural from '../../../utilities/plural';

type PriorEntriesTableProps = {
    priorWorksheet: ReconciliationWorksheet | GenericWorksheet;
    currentWorksheet: ReconciliationWorksheet | GenericWorksheet;
    sort: string;
};

const columns: DataGridColumn[] = [
    {
        header: 'Date',
        width: '1fr',
        alignment: 'left',
    },
    {
        header: 'Reference',
        width: ' 0.5fr',
        alignment: 'left',
    },
    {
        header: 'Description',
        width: '2fr',
        alignment: 'left',
    },
    {
        header: 'Attachments',
        width: '1fr',
        alignment: 'center',
    },
];

const genColumns: DataGridColumn[] = [
    ...columns,
    {
        header: '',
        width: '0fr',
        alignment: 'right',
    },
];

const recColumns: DataGridColumn[] = [
    ...columns,
    {
        header: 'Amount',
        width: '2fr',
        alignment: 'right',
    },
];

/**
 * Determines the appropriate toast heading based on carry forward operation results
 * @param successCount - Number of successfully carried forward entries
 * @param noErrors - Whether there were any errors during the operation
 * @returns Toast heading text based on operation outcome
 */
const getToastHeading = (successCount: number, noErrors: boolean): string => {
    if (successCount > 0 && noErrors) return 'Carried Forward';
    if (successCount === 0 && !noErrors) return 'Carry Forward Error';
    return 'Partial Success';
};

/**
 * Generates toast message text describing carry forward operation results
 * @param successCount - Number of successfully carried forward entries
 * @param errorCount - Number of entries that failed to carry forward
 * @param noErrors - Whether there were any errors during the operation
 * @returns Formatted message describing operation outcome
 */
const getToastMessage = (successCount: number, errorCount: number, noErrors: boolean): string => {
    if (successCount > 0 && noErrors) {
        return `${successCount} ${plural(successCount, 'entry', 'entries')} carried forward successfully.`;
    }
    if (successCount === 0 && errorCount > 0) {
        return 'Failed to carry forward selected entries. Please try again.';
    }
    return (
        `${successCount} ${plural(successCount, 'entry', 'entries')} carried forward. ` +
        `${errorCount} ${plural(errorCount, 'entry', 'entries')} failed`
    );
};

function PriorEntriesTable({ priorWorksheet, currentWorksheet, sort }: PriorEntriesTableProps): JSX.Element {
    const { trackEvent } = useIntercom();
    const priorEntriesQuery = useEntriesV2(priorWorksheet.id, { sort });
    const currentEntriesQuery = useEntriesV2(currentWorksheet.id);
    const priorWorkpaperQuery = useWorkpaper(priorWorksheet.workpaperId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const currentEntries = currentEntriesQuery.data?.data ?? [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const priorEntries = priorEntriesQuery.data?.data ?? [];

    const priorWorkpaper = priorWorkpaperQuery.data?.workpaper;

    const [selectedEntries, setSelectedEntries] = useState<string[]>([]);

    const validCarryForwardEntries = useMemo(() => {
        return selectedEntries.filter((selectedId) => {
            const entry = priorEntries.find((e) => e.id === selectedId);
            return !entry?.source && !currentEntries.some((ce) => ce.priorEntryId === selectedId);
        });
    }, [selectedEntries, priorEntries, currentEntries]);

    const [viewingAttachmentsEntryId, setViewingAttachmentsEntryId] = useState<string>();
    const tableRef = useRef<DataGridRef>(null);
    const carryForwardQuery = useCarryForward();

    function handleCarryForwardEntries(entries: string[]): void {
        const payloads = entries.map((entryId) => {
            return { type: ResourceType.ENTRY, priorResourceId: entryId };
        });
        carryForwardQuery.mutate(
            {
                priorWorkpaperId: priorWorksheet.workpaperId,
                currentWorkpaperId: currentWorksheet.workpaperId,
                payloads,
            },
            {
                onSuccess: (res) => {
                    const successCount = res.filter((r) => r.outcome === 'success').length;
                    const errorCount = res.filter((r) => r.outcome === 'error').length;
                    // trigger intercom event
                    if (successCount > 0) {
                        trackEvent(
                            intercomEvents.carryForward.carryForwardWorksheetEntries.event,
                            intercomEvents.carryForward.carryForwardWorksheetEntries.metaData(
                                currentWorksheet.workpaperId,
                                currentWorksheet.id,
                                priorWorksheet.id,
                                successCount
                            )
                        );
                    }
                    const noErrors = errorCount === 0;

                    const toastHeading = getToastHeading(successCount, noErrors);
                    const toastMessage = getToastMessage(successCount, errorCount, noErrors);
                    toastNotification(`${successCount > 0 ? 'success' : 'error'}`, toastHeading, toastMessage);
                    setSelectedEntries([]);
                    tableRef.current?.clear();
                },
                onError: () => {
                    toastNotification('error', 'Carry Forward Error!', `Carrying forward selected entries failed. Please try again.`);
                },
            }
        );
    }

    if (priorEntriesQuery.isLoading) {
        return (
            <div className="flex-1 h-full flex items-center justify-center">
                <Spinner />
            </div>
        );
    }

    if (!priorEntriesQuery.isSuccess || priorEntriesQuery.isError || !priorEntriesQuery.data) {
        return <div className="flex-1 h-full flex items-center justify-center">Something went wrong!</div>;
    }

    return (
        <div className="mb-10" data-intercom-target="cf-prior-entries">
            <div className="flex items-center my-4">
                <div className="flex-grow h-px bg-gray-300"></div>
                <span className="mx-2 text-xs text-gray-500">{`${
                    priorWorkpaper?.periodEnd ? `${formatDate(priorWorkpaper?.periodEnd)}` : 'Previous Period'
                }`}</span>
                <div className="flex-grow h-px bg-gray-300"></div>
            </div>
            <div className="flex justify-end items-center mb-4">
                <div className="flex items-center gap-2" data-intercom-target="cf-prior-entries-cf-action">
                    <ButtonV2
                        leftIcon={<ForwardIcon className="stroke-2" />}
                        size="sm"
                        label={`Carry Forward ${validCarryForwardEntries.length > 0 ? `(${validCarryForwardEntries.length})` : ''}`}
                        type="secondary"
                        isDisabled={validCarryForwardEntries.length === 0}
                        onClick={(): void => handleCarryForwardEntries(validCarryForwardEntries)}
                    />
                </div>
            </div>
            <div>
                <DataGrid
                    columns={priorWorksheet.type === WorksheetTypeEnum.GENERIC ? genColumns : recColumns}
                    rowSizes={ROW_SIZES}
                    totalRows={priorEntries.length}
                    disablePagination
                    onSelect={setSelectedEntries}
                    emptyMessage="No entries"
                    ref={tableRef}
                >
                    {priorEntries.map((entry) => {
                        return (
                            <EntryRow
                                key={entry.id}
                                entry={entry}
                                id={entry.id}
                                worksheet={priorWorksheet}
                                preventDrag={true}
                                disabled={true}
                                disableAttachments={true}
                                priorEntry={true}
                                onAttach={(): void => {}}
                                showAttachmentWindow={viewingAttachmentsEntryId === entry.id}
                                onAttachmentWindowToggle={(open): void => {
                                    setViewingAttachmentsEntryId(open ? entry.id : undefined);
                                }}
                            />
                        );
                    })}
                </DataGrid>
                {priorWorksheet.type === WorksheetTypeEnum.RECONCILIATION && priorWorksheet.entries.length > 0 && (
                    <EntriesSubtotal entries={priorEntries} />
                )}
                {priorWorksheet.entries.length === 0 && <NoEntriesMessage type={priorWorksheet.type} onClick={(): void => {}} />}
            </div>
        </div>
    );
}

export default PriorEntriesTable;
